import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Filter, FileText, Newspaper } from 'lucide-react';
import axios from 'axios';

const CATEGORY_COLORS = {
  'Fato Relevante':                      'bg-rose-500/10 text-rose-500 border-rose-500/20',
  'Comunicado ao Mercado':               'bg-sky-500/10 text-sky-500 border-sky-500/20',
  'Assembleia':                          'bg-violet-500/10 text-violet-500 border-violet-500/20',
  'Reunião da Administração':            'bg-amber-500/10 text-amber-500 border-amber-500/20',
  'Aviso aos Acionistas':                'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  'Dados Econômico-Financeiros':         'bg-blue-500/10 text-blue-500 border-blue-500/20',
  'Relatório Proventos':                 'bg-green-500/10 text-green-500 border-green-500/20',
};

function categoryBadge(cat) {
  const cls = CATEGORY_COLORS[cat] ?? 'bg-[var(--accent-soft)] text-[var(--muted)] border-[var(--border)]';
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cls} whitespace-nowrap`}>
      {cat}
    </span>
  );
}

function fmtDate(str) {
  if (!str) return '—';
  return str.slice(0, 10);
}

const PAGE_SIZE = 50;

export default function AnnouncementsTab({ lang, cdcvm }) {
  const navigate     = useNavigate();
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [activeCat,  setActiveCat]  = useState(null);
  const [offset,     setOffset]     = useState(0);

  const fetchData = useCallback(() => {
    if (!cdcvm) return;
    setLoading(true);
    setError(null);
    const params = { limit: PAGE_SIZE, offset };
    if (activeCat) params.cat = activeCat;
    axios.get(`/stocks/${cdcvm}/announcements`, { params })
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error ?? e.message))
      .finally(() => setLoading(false));
  }, [cdcvm, activeCat, offset]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset offset when filter changes
  useEffect(() => { setOffset(0); }, [activeCat, cdcvm]);

  const categories = data?.categories ?? [];
  const rows       = data?.rows       ?? [];
  const total      = data?.total      ?? 0;
  const pageCount  = Math.ceil(total / PAGE_SIZE);
  const page       = Math.floor(offset / PAGE_SIZE);

  return (
    <div className="space-y-5">

      {/* Category filter pills */}
      <div className="bg-[var(--card)] border border-[var(--border)] rounded-2xl p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={13} className="text-[var(--muted)]" />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--muted)]">
            {lang === 'pt' ? 'Filtrar por categoria' : 'Filter by category'}
          </span>
          {activeCat && (
            <button
              onClick={() => setActiveCat(null)}
              className="ml-auto text-[10px] text-[var(--muted)] hover:text-[var(--text)] underline"
            >
              {lang === 'pt' ? 'limpar' : 'clear'}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {categories.map(c => {
            const isActive = activeCat === c.categoria;
            const colorCls = CATEGORY_COLORS[c.categoria]
              ?? 'bg-[var(--accent-soft)] text-[var(--muted)] border-[var(--border)]';
            return (
              <button
                key={c.categoria}
                onClick={() => setActiveCat(isActive ? null : c.categoria)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${
                  isActive
                    ? colorCls + ' ring-1 ring-offset-0 opacity-100'
                    : 'bg-[var(--accent-soft)] border-[var(--border)] text-[var(--muted)] hover:text-[var(--text)]'
                }`}
              >
                {c.categoria}
                <span className="opacity-60">{c.total}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div className="bg-[var(--card)] border border-[var(--border)] rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border)]">
          <div>
            <p className="text-sm font-bold text-[var(--text)]">
              {lang === 'pt' ? 'Comunicados & Divulgações' : 'Announcements & Disclosures'}
            </p>
            <p className="text-[10px] text-[var(--muted)] mt-0.5">
              {lang === 'pt' ? `Fonte: CVM IPE — ${total} registros` : `Source: CVM IPE — ${total} records`}
            </p>
          </div>
          {total > PAGE_SIZE && (
            <div className="flex items-center gap-1.5">
              <button
                disabled={page === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                className="px-2.5 py-1 rounded-lg text-[10px] font-semibold border border-[var(--border)] text-[var(--muted)] hover:text-[var(--text)] disabled:opacity-30 transition-all"
              >
                ←
              </button>
              <span className="text-[10px] text-[var(--muted)]">{page + 1} / {pageCount}</span>
              <button
                disabled={page >= pageCount - 1}
                onClick={() => setOffset(offset + PAGE_SIZE)}
                className="px-2.5 py-1 rounded-lg text-[10px] font-semibold border border-[var(--border)] text-[var(--muted)] hover:text-[var(--text)] disabled:opacity-30 transition-all"
              >
                →
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-7 h-7 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-32 px-6">
            <p className="text-sm text-rose-500">{error}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 gap-2">
            <p className="text-sm text-[var(--muted)]">
              {lang === 'pt' ? 'Nenhum comunicado encontrado.' : 'No announcements found.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--accent-soft)]">
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--muted)] w-24">
                    {lang === 'pt' ? 'Data' : 'Date'}
                  </th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--muted)]">
                    {lang === 'pt' ? 'Categoria' : 'Category'}
                  </th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--muted)]">
                    {lang === 'pt' ? 'Assunto' : 'Subject'}
                  </th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--muted)] hidden md:table-cell">
                    {lang === 'pt' ? 'Resumo / Tipo' : 'Summary / Type'}
                  </th>
                  <th className="px-4 py-2.5 w-8" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr
                    key={row.protocolo ?? i}
                    className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--accent-soft)] transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-[var(--muted)] whitespace-nowrap align-top">
                      {fmtDate(row.data_entrega || row.data)}
                    </td>
                    <td className="px-4 py-3 align-top">
                      {categoryBadge(row.categoria)}
                    </td>
                    <td className="px-4 py-3 text-[var(--text)] leading-snug max-w-xs align-top">
                      <span className="font-medium">{row.assunto || row.tipo || '—'}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell align-top max-w-sm">
                      {row.snippet ? (
                        <div>
                          <p className="text-[11px] text-[var(--text)] leading-relaxed line-clamp-4">
                            {row.snippet}
                          </p>
                          <div className="mt-1.5 flex items-center gap-2.5">
                            {row.word_count && (
                              <span className="inline-flex items-center gap-1 text-[9px] text-[var(--muted)] opacity-60">
                                <FileText size={9} />
                                {row.word_count.toLocaleString()} palavras
                              </span>
                            )}
                            {row.news_slug && (
                              <button
                                onClick={() => navigate(`/noticias/${row.news_slug}`)}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors"
                              >
                                <Newspaper size={9} />
                                {lang === 'pt' ? 'Ver notícia' : 'Read article'}
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-[var(--muted)] whitespace-nowrap">{row.tipo_apresentacao || '—'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center align-top">
                      {row.link ? (
                        <a
                          href={row.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={lang === 'pt' ? 'Abrir documento CVM' : 'Open CVM document'}
                          className="text-[var(--muted)] hover:text-emerald-500 transition-colors inline-flex"
                        >
                          <ExternalLink size={13} />
                        </a>
                      ) : (
                        <span className="text-[var(--muted)] opacity-30">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
